<script setup lang="ts">
import LinkIcon from '@/assets/data/LinkIcon.vue'
import UIToolTip from '@/components/ui/UIToolTip.vue'
import { TButtonColor, TButtonVariant } from '@lifk/types'
import {
  PhArrowClockwise,
  PhArrowCounterClockwise,
  PhArrowLeft,
  PhArrowRight,
  PhArrowsOut,
  PhBookOpen,
  PhCheck,
  PhCopySimple,
  PhHouse,
  PhList,
  PhMagnifyingGlass,
  PhNote,
  PhPencilLine,
  PhPenNibStraight,
  PhPlay,
  PhPlus,
  PhPolygon,
  PhQuestion,
  PhShareFat,
  PhTrash
} from '@phosphor-icons/vue'
import { cva } from 'class-variance-authority'
import { computed } from 'vue'

// TODO: Rename to UIButton (separate small PR to reduce clutter)

const props = withDefaults(
  defineProps<{
    /** What icon should be displayed in the button? */
    icon?: keyof typeof iconMapping
    /** What text should be displayed in the button?<br/>(Can also be provided via the slot.) */
    text?: string
    /** What visual style should be used for the button? */
    variant?: TButtonVariant
    /** What color should be used for the button? */
    color?: TButtonColor
    /** Should the button's coloring be adjusted for use on a dark background? */
    inverse?: boolean
    /** Is the button currently selected? */
    selected?: boolean
    /** Should the icon be displayed on the right side of the text? */
    iconOnRight?: boolean
    /** Should a router link be used to render the button (allows users to interact with the button as a link)? */
    routerLink?: boolean
    /** What path should the user navigate to when the button is clicked (only used if routerLink is true)? */
    path?: string
    /** What target should be used for the router link (only used if routerLink is true)? */
    target?: string
    /** Is the button disabled? */
    disabled?: boolean
    /** What HTML control type should be applied to the button? */
    type?: 'submit' | 'button'
    /** Is the button exclusively an icon? (props.text will be moved into a tooltip) */
    iconOnly?: boolean
    /** Does the button have additional text to show in a tooltip? */
    tooltipText?: string
  }>(),
  {
    variant: 'ghost',
    color: 'neutral',
    inverse: false,
    selected: false,
    iconOnRight: false,
    routerLink: false,
    path: '',
    target: '',
    disabled: false,
    type: 'button',
    iconOnly: false
  }
)

const iconMapping = {
  ArrowLeft: PhArrowLeft,
  ArrowRight: PhArrowRight,
  ArrowsOut: PhArrowsOut,
  Book: PhBookOpen,
  Check: PhCheck,
  Copy: PhCopySimple,
  Draw: PhPenNibStraight,
  House: PhHouse,
  Link: LinkIcon,
  List: PhList,
  Note: PhNote,
  Play: PhPlay,
  Plus: PhPlus,
  Pencil: PhPencilLine,
  Polygon: PhPolygon,
  Question: PhQuestion,
  Refresh: PhArrowCounterClockwise,
  Rotate: PhArrowClockwise,
  Search: PhMagnifyingGlass,
  Share: PhShareFat,
  Trash: PhTrash
}

const iconWeights = {
  Play: 'fill'
}

const buttonStyles = cva('', {
  variants: {
    variant: {
      filled: '',
      outline: 'border border-solid',
      ghost: ''
    },
    color: {
      primary: '',
      neutral: '',
      success: '',
      warning: '',
      destructive: ''
    },
    inverse: {
      true: '',
      false: ''
    },
    selected: {
      true: '',
      false: ''
    }
  },
  compoundVariants: [
    // primary / filled
    {
      variant: 'filled',
      color: 'primary',
      inverse: false,
      selected: false,
      class:
        'text-emphasis disabled:text-disabled bg-poppy hover:bg-poppy-tint-2 active:bg-poppy-tone-1 disabled:bg-poppy-tint-3 disabled:opacity-60'
    },
    // primary / filled / selected
    {
      variant: 'filled',
      color: 'primary',
      inverse: false,
      selected: true,
      class:
        'text-emphasis disabled:text-disabled bg-poppy hover:bg-poppy-tint-2 active:bg-poppy-tone-1 disabled:bg-poppy-tint-3 disabled:opacity-60'
    },
    // primary / filled / inverse
    {
      variant: 'filled',
      color: 'primary',
      inverse: true,
      selected: false,
      class:
        'text-emphasis disabled:text-disabled bg-poppy hover:bg-poppy-tint-2 active:bg-poppy-tone-1 disabled:bg-poppy-tint-3 disabled:opacity-60'
    },
    // neutral / filled
    {
      variant: 'filled',
      color: 'neutral',
      inverse: false,
      selected: false,
      class:
        'text-emphasis disabled:text-disabled bg-stone hover:bg-hover active:bg-stone-tone-2 disabled:bg-transparent disabled:opacity-60'
    },
    // neutral / filled / selected
    {
      variant: 'filled',
      color: 'neutral',
      inverse: false,
      selected: true,
      class:
        'text-emphasis disabled:text-disabled bg-poppy hover:bg-poppy-tint-2 active:bg-stone-tone-2 disabled:bg-transparent disabled:opacity-60'
    },
    // neutral / outline
    {
      variant: 'outline',
      color: 'neutral',
      inverse: false,
      selected: false,
      class:
        'text-emphasis disabled:text-disabled bg-transparent hover:bg-hover active:bg-stone-tone-2 border-default hover:border-default active:border-default disabled:opacity-60'
    },
    // neutral / outline / selected
    {
      variant: 'outline',
      color: 'neutral',
      inverse: false,
      selected: true,
      class:
        'text-emphasis disabled:text-disabled bg-transparent hover:bg-hover active:bg-stone-tone-2 border-default hover:border-default active:border-default disabled:opacity-60'
    },
    // neutral / outline / inverse
    {
      variant: 'outline',
      color: 'neutral',
      inverse: true,
      selected: false,
      class:
        'text-inverse hover:text-emphasis active:text-emphasis disabled:text-disabled bg-transparent hover:bg-hover active:bg-stone-tone-2 border-inverse hover:border-stone-tone-1 active:border-stone-tone-2 disabled:opacity-60'
    },
    // neutral / ghost
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: false,
      selected: false,
      class:
        'text-emphasis disabled:text-disabled hover:bg-hover active:bg-stone-tone-2 disabled:opacity-60'
    },
    // neutral / ghost / selected
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: false,
      selected: true,
      class:
        'text-emphasis disabled:text-disabled hover:bg-hover active:bg-stone-tone-2 disabled:opacity-60'
    },
    // neutral / ghost / inverse
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: true,
      selected: false,
      class:
        'text-inverse hover:text-emphasis active:text-emphasis disabled:text-disabled hover:bg-hover active:bg-stone-tone-2 disabled:opacity-60'
    },
    // destructive / filled
    {
      variant: 'filled',
      color: 'destructive',
      inverse: false,
      selected: false,
      class:
        'text-inverse bg-error hover:bg-error-tint-1 active:bg-error-tone-1'
    },
    // destructive / filled / selected
    {
      variant: 'filled',
      color: 'destructive',
      inverse: false,
      selected: true,
      class:
        'text-inverse bg-error hover:bg-error-tint-1 active:bg-error-tone-1'
    },
    // destructive / outline
    {
      variant: 'outline',
      color: 'destructive',
      inverse: false,
      selected: false,
      class:
        'text-error hover:text-error-tint-1 active:text-error-tone-1 border-error hover:border-error-tint-1 active:border-error-tone-1'
    },
    // destructive / outline / selected
    {
      variant: 'outline',
      color: 'destructive',
      inverse: false,
      selected: true,
      class:
        'text-error hover:text-error-tint-1 active:text-error-tone-1 border-error hover:border-error-tint-1 active:border-error-tone-1'
    },
    // success / filled
    {
      variant: 'filled',
      color: 'success',
      inverse: false,
      selected: false,
      class:
        'text-inverse bg-success hover:bg-success-tint-1 active:bg-success-tone-3'
    },
    // success / filled / selected
    {
      variant: 'filled',
      color: 'success',
      inverse: false,
      selected: true,
      class:
        'text-inverse bg-success hover:bg-success-tint-1 active:bg-success-tone-3'
    },
    // success / outline
    {
      variant: 'outline',
      color: 'success',
      inverse: false,
      selected: false,
      class:
        'text-success hover:text-success-tint-1 active:text-success-tone-3 border-success hover:border-success-tint-1 active:border-success-tone-3'
    },
    // warning / filled
    {
      variant: 'filled',
      color: 'warning',
      inverse: false,
      selected: false,
      class:
        'text-emphasis bg-warning hover:bg-warning-tint-1 active:bg-warning-tone-1'
    },
    // warning / filled / selected
    {
      variant: 'filled',
      color: 'warning',
      inverse: false,
      selected: true,
      class:
        'text-emphasis bg-warning hover:bg-warning-tint-1 active:bg-warning-tone-1'
    }
  ]
})

const classes = computed(() => {
  return buttonStyles({
    variant: props.variant,
    color: props.color,
    inverse: props.inverse,
    selected: props.selected
  })
})

const iconStyles = cva('', {
  variants: {
    variant: {
      filled: '',
      outline: '',
      ghost: ''
    },
    color: {
      primary: '',
      neutral: '',
      success: '',
      warning: '',
      destructive: ''
    },
    inverse: {
      true: '',
      false: ''
    },
    selected: {
      true: '',
      false: ''
    }
  },
  compoundVariants: [
    // primary / filled
    {
      variant: 'filled',
      color: 'primary',
      inverse: false,
      selected: false,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // primary / filled / selected
    {
      variant: 'filled',
      color: 'primary',
      inverse: false,
      selected: true,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // primary / filled / inverse
    {
      variant: 'filled',
      color: 'primary',
      inverse: true,
      selected: false,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / filled
    {
      variant: 'filled',
      color: 'neutral',
      inverse: false,
      selected: false,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / filled / selected
    {
      variant: 'filled',
      color: 'neutral',
      inverse: false,
      selected: true,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / outline
    {
      variant: 'outline',
      color: 'neutral',
      inverse: false,
      selected: false,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / outline / selected
    {
      variant: 'outline',
      color: 'neutral',
      inverse: false,
      selected: true,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / outline / inverse
    {
      variant: 'outline',
      color: 'neutral',
      inverse: true,
      selected: false,
      class:
        'fill-white group-hover:fill-charcoal group-active:fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / ghost
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: false,
      selected: false,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / ghost / selected
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: false,
      selected: true,
      class: 'fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // neutral / ghost / inverse
    {
      variant: 'ghost',
      color: 'neutral',
      inverse: true,
      selected: false,
      class:
        'fill-white group-hover:fill-charcoal group-active:fill-charcoal group-disabled:fill-slate-tint-3'
    },
    // destructive / filled
    {
      variant: 'filled',
      color: 'destructive',
      inverse: false,
      selected: false,
      class: 'fill-white'
    },
    // destructive / filled / selected
    {
      variant: 'filled',
      color: 'destructive',
      inverse: false,
      selected: true,
      class: 'fill-white'
    },
    // destructive / outline
    {
      variant: 'outline',
      color: 'destructive',
      inverse: false,
      selected: false,
      class:
        'fill-error group-hover:fill-error-tint-1 group-active:fill-error-tone-1'
    },
    // destructive / outline / selected
    {
      variant: 'outline',
      color: 'destructive',
      inverse: false,
      selected: true,
      class:
        'fill-error group-hover:fill-error-tint-1 group-active:fill-error-tone-1'
    },
    // success / filled
    {
      variant: 'filled',
      color: 'success',
      inverse: false,
      selected: false,
      class: 'fill-white'
    },
    // success / filled / selected
    {
      variant: 'filled',
      color: 'success',
      inverse: false,
      selected: true,
      class: 'fill-white'
    },
    // success / outline
    {
      variant: 'outline',
      color: 'success',
      inverse: false,
      selected: false,
      class:
        'fill-success group-hover:fill-success-tint-1 group-active:fill-success-tone-3'
    },
    // warning / filled
    {
      variant: 'filled',
      color: 'warning',
      inverse: false,
      selected: false,
      class: 'fill-charcoal'
    },
    // warning / filled / selected
    {
      variant: 'filled',
      color: 'warning',
      inverse: false,
      selected: true,
      class: 'fill-charcoal'
    }
  ]
})

const iconClasses = computed(() => {
  return iconStyles({
    variant: props.variant,
    color: props.color,
    inverse: props.inverse,
    selected: props.selected
  })
})

const tooltipId = `ui-button-tooltip-${crypto.randomUUID()}`

const showTooltip = computed(() => {
  const isIconOnly = props.iconOnly && !!(props.tooltipText || props.text)
  const hasAdditionalTooltipText = !!props.tooltipText
  return isIconOnly || hasAdditionalTooltipText
})
</script>

<template>
  <div>
    <UIToolTip
      v-if="showTooltip"
      :id="tooltipId"
      :text="tooltipText ?? text"
      direction="top"
      alignment="center"
    />

    <teleport defer :to="`#${tooltipId}`" :disabled="!showTooltip">
      <component
        :is="routerLink ? 'router-link' : 'button'"
        :to="path"
        :target="target"
        :type="type"
        :disabled="disabled"
        :tabindex="disabled ? -1 : 0"
        :aria-label="text"
        class="ui-text-button w-full whitespace-nowrap inline-flex gap-1 justify-center rounded text-body items-center font-sans font-medium disabled:opacity-60 transition-colors duration-100 group"
        :class="[
          classes,
          iconOnly ? 'p-[10px]' : 'px-3 py-2',
          iconOnRight ? 'flex-row-reverse' : 'flex-row',
          disabled ? 'pointer-events-none' : ''
        ]"
      >
        <component
          :is="iconMapping[icon]"
          v-if="icon"
          class="h-4 w-4"
          :class="iconClasses"
          :weight="iconWeights[icon] ?? 'regular'"
        ></component>
        <span
          v-if="text && !iconOnly"
          class="max-w-md hover:opacity-100 font-medium"
          >{{ text }}</span
        >
        <slot v-else></slot>
      </component>
    </teleport>
  </div>
</template>

<style scoped>
.ui-text-button {
  transition: background-color 0.1s;
}
.ui-text-button:active {
  transition: background-color 0.05s;
}
</style>
